import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Settings,
  Shield,
  Eye,
  EyeOff,
  RefreshCw,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const SettingsPage = () => {
  const navigate = useNavigate();
  useEffect(() => { if (!localStorage.getItem('reddyannabook_token')) navigate('/login'); }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // OTP for password change
  const [pwStep, setPwStep] = useState(1); // 1=form, 2=otp
  const [otp, setOtp] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [pwMsg, setPwMsg] = useState(null); // {type:'success'|'error', text:'...'}
  const [otpTimer, setOtpTimer] = useState(0);
  const otpTimerRef = React.useRef(null);

  const startOtpTimer = () => {
    setOtpTimer(60);
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    otpTimerRef.current = setInterval(() => {
      setOtpTimer(prev => { if (prev <= 1) { clearInterval(otpTimerRef.current); return 0; } return prev - 1; });
    }, 1000);
  };

  // Step 1: Validate form + send OTP
  const handlePasswordChange = async () => {
    setPwMsg(null);
    if (!currentPassword || !newPassword || !confirmPassword) { setPwMsg({type:'error',text:'All fields are required'}); return; }
    if (newPassword !== confirmPassword) { setPwMsg({type:'error',text:'New passwords do not match'}); return; }
    if (newPassword.length < 6) { setPwMsg({type:'error',text:'Password must be at least 6 characters'}); return; }

    setSendingOtp(true);
    try {
      const token = localStorage.getItem('reddyannabook_token');
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ phone: '0000000000', purpose: 'change_password' })
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { success: false, message: 'Server error. Please try again.' }; }
      if (data.success) {
        setPwStep(2);
        startOtpTimer();
        setPwMsg({type:'success',text:'OTP sent to your WhatsApp!'});
      } else {
        setPwMsg({type:'error',text: data.message || 'Failed to send OTP'});
      }
    } catch (e) { setPwMsg({type:'error',text: e.message || 'Network error. Please try again.'}); }
    finally { setSendingOtp(false); }
  };

  // Step 2: Verify OTP + change password
  const handleVerifyAndChange = async () => {
    setPwMsg(null);
    if (otp.length < 4) { setPwMsg({type:'error',text:'Enter the OTP sent to your WhatsApp'}); return; }

    setVerifyingOtp(true);
    try {
      const token = localStorage.getItem('reddyannabook_token');
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword, otp })
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { success: false, message: 'Server error. Please try again.' }; }
      if (data.success) {
        setPwMsg({type:'success',text:'Password changed successfully!'});
        setPwStep(1); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setOtp('');
        if (otpTimerRef.current) clearInterval(otpTimerRef.current);
      } else {
        setPwMsg({type:'error',text: data.message || 'Failed to change password'});
      }
    } catch (e) { setPwMsg({type:'error',text: e.message || 'Network error. Please try again.'}); }
    finally { setVerifyingOtp(false); }
  };

  const handleResendOtp = () => { handlePasswordChange(); };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Settings className="w-8 h-8 mr-3 text-blue-600" />
            Settings
          </h1>
          <p className="text-gray-600 mt-2">Manage your account preferences and security settings</p>
        </div>

        <div className="grid gap-6">
          {/* Password Change — 2-step with OTP */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-red-600" />
                Change Password
              </CardTitle>
              <CardDescription>
                {pwStep === 1 ? 'Update your account password' : 'Enter OTP sent to your WhatsApp'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status message */}
              {pwMsg && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${pwMsg.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {pwMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {pwMsg.text}
                </div>
              )}

              {pwStep === 1 ? (
                <>
                  <div>
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input id="newPassword" type={showNewPass ? "text" : "password"} value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" className="pr-10" />
                      <button type="button" onClick={() => setShowNewPass(!showNewPass)} className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {showNewPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Input id="confirmPassword" type={showConfirmPass ? "text" : "password"} value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="pr-10" />
                      <button type="button" onClick={() => setShowConfirmPass(!showConfirmPass)} className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {showConfirmPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button onClick={handlePasswordChange} disabled={sendingOtp} className="w-full">
                    {sendingOtp ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending OTP...</> : 'Change Password'}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    A 6-digit OTP has been sent to your registered WhatsApp number. Enter it below to confirm your password change.
                  </p>
                  <div>
                    <Label htmlFor="otp">WhatsApp OTP</Label>
                    <Input id="otp" type="number" value={otp} onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                      placeholder="Enter 6-digit OTP" className="text-center text-xl tracking-widest" />
                  </div>
                  <div className="text-center text-sm text-gray-500">
                    {otpTimer > 0 ? (
                      <span>Resend in {otpTimer}s</span>
                    ) : (
                      <button onClick={handleResendOtp} disabled={sendingOtp} className="text-green-600 font-semibold hover:underline">
                        {sendingOtp ? 'Sending...' : 'Resend OTP'}
                      </button>
                    )}
                  </div>
                  <Button onClick={handleVerifyAndChange} disabled={verifyingOtp || otp.length < 4} className="w-full bg-green-700 hover:bg-green-800">
                    {verifyingOtp ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : '✅ Verify & Change Password'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setPwStep(1); setOtp(''); setPwMsg(null); if (otpTimerRef.current) clearInterval(otpTimerRef.current); }} className="w-full text-gray-500">
                    ← Go Back
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
